// HelpHint — zdieľaná inline nápoveda (ústava 87.4 + 12.0 bod 4)
// Každé pole/akcia: ČO/PREČO/KAM sa zapisuje + odkaz do manuálu. Používateľ bez školenia
// klikne „?" → vysvetlenie + „📖 Manuál" otvorí MATRIX_MANUAL na danej sekcii.
// Použitie: <HelpHint text="Čo/prečo/kam…" section="86.2" />
import React, { useState, useEffect, useMemo } from 'react'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import { Modal } from './Modal.jsx'

const MANUAL_URL = '/uploads/MATRIX_MANUAL.md'
let _manualCache = null

// ── inline **bold** + `code` ──
function fmt(text, key) {
  const parts = []
  let rest = String(text), i = 0
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/
  let m
  while ((m = rest.match(re))) {
    if (m.index > 0) parts.push(rest.slice(0, m.index))
    if (m[2] != null) parts.push(<strong key={key + '-b' + i}>{m[2]}</strong>)
    else parts.push(<code key={key + '-c' + i} style={{ fontFamily: TYPO.mono, background: COLORS.bgSoft, padding: '0 4px', borderRadius: 3, fontSize: '0.92em' }}>{m[3]}</code>)
    rest = rest.slice(m.index + m[0].length); i++
  }
  if (rest) parts.push(rest)
  return parts
}
// hinty_v1: text nápovedy nesie odseky — raz ako skutočný nový riadok, raz ako dvojznak zo zdroja obrazovky.
// Zápis cez fromCharCode je schválný: v JSX atribúte sa spätné lomítko cestou medzi nástrojmi stráca.
const _NL = String.fromCharCode(10), _BS = String.fromCharCode(92)
const riadkyHintu = (t) => String(t == null ? '' : t).split(_BS + 'n').join(_NL).split(_NL)
const secId = (heading) => { const m = String(heading).match(/^(\d+(?:\.\d+)*)/); return m ? m[1] : null }

// ── ľahký markdown → bloky ──
function parseManual(md) {
  const lines = md.split('\n'), out = []
  let list = [], table = []
  const flushL = () => { if (list.length) { out.push({ t: 'ul', items: list }); list = [] } }
  const flushT = () => { if (table.length) { out.push({ t: 'table', rows: table.map(r => r.split('|').slice(1, -1).map(c => c.trim())) }); table = [] } }
  for (const line of lines) {
    if (/^\s*\|/.test(line)) { flushL(); if (!/^\s*\|[\s:|-]+\|?\s*$/.test(line)) table.push(line.trim()); continue }
    flushT()
    if (/^### /.test(line)) { flushL(); out.push({ t: 'h3', x: line.slice(4) }) }
    else if (/^## /.test(line)) { flushL(); out.push({ t: 'h2', x: line.slice(3) }) }
    else if (/^# /.test(line)) { flushL(); out.push({ t: 'h2', x: line.slice(2) }) }
    else if (/^> /.test(line)) { flushL(); out.push({ t: 'quote', x: line.slice(2) }) }
    else if (/^\s*[-*] /.test(line)) { list.push(line.replace(/^\s*[-*] /, '')) }
    else if (/^---+\s*$/.test(line)) { flushL(); out.push({ t: 'hr' }) }
    else if (line.trim() === '') { flushL() }
    else { flushL(); out.push({ t: 'p', x: line }) }
  }
  flushL(); flushT()
  return out
}

function ManualModal({ open, section, onClose }) {
  const [md, setMd] = useState(_manualCache)
  useEffect(() => {
    if (!open || _manualCache) return
    fetch(MANUAL_URL).then(r => r.ok ? r.text() : '').then(t => { _manualCache = t; setMd(t) }).catch(() => setMd('Manuál sa nepodarilo načítať.'))
  }, [open])
  const blocks = useMemo(() => md ? parseManual(md) : [], [md])
  useEffect(() => {
    if (!open || !section || !blocks.length) return
    const t = setTimeout(() => {
      let s = String(section)
      while (s) {                                   // 86.2 → 86 fallback (manuál nemusí mať podsekciu)
        const el = document.getElementById('man-' + s)
        if (el) { el.scrollIntoView({ block: 'start', behavior: 'smooth' }); return }
        if (!s.includes('.')) break
        s = s.replace(/\.\d+$/, '')
      }
    }, 120)
    return () => clearTimeout(t)
  }, [open, section, blocks])
  if (!open) return null
  return (
    <Modal open title="Manuál Matrix" onClose={onClose} width={820}>
      <div style={{ maxHeight: '70vh', overflow: 'auto', fontSize: TYPO.sizes.sm, lineHeight: 1.6, color: COLORS.text }}>
        {!md ? <div style={{ color: COLORS.muted, padding: SPACE.lg }}>Načítavam…</div> : blocks.map((b, i) => {
          if (b.t === 'h2') return <h2 key={i} id={secId(b.x) ? 'man-' + secId(b.x) : undefined} style={{ fontSize: TYPO.sizes.lg, marginTop: SPACE.lg, marginBottom: SPACE.xs, color: COLORS.brandD || COLORS.nav, borderBottom: `2px solid ${COLORS.line}`, paddingBottom: 4 }}>{fmt(b.x, i)}</h2>
          if (b.t === 'h3') return <h3 key={i} id={secId(b.x) ? 'man-' + secId(b.x) : undefined} style={{ fontSize: TYPO.sizes.md, marginTop: SPACE.md, marginBottom: 2, color: COLORS.nav }}>{fmt(b.x, i)}</h3>
          if (b.t === 'quote') return <div key={i} style={{ borderLeft: `3px solid ${COLORS.brand}`, padding: '4px 10px', background: COLORS.bgSoft, color: COLORS.muted, margin: '4px 0' }}>{fmt(b.x, i)}</div>
          if (b.t === 'hr') return <hr key={i} style={{ border: 'none', borderTop: `1px solid ${COLORS.line}`, margin: `${SPACE.md}px 0` }} />
          if (b.t === 'ul') return <ul key={i} style={{ margin: '4px 0', paddingLeft: 22 }}>{b.items.map((it, j) => <li key={j} style={{ marginBottom: 2 }}>{fmt(it, i + '-' + j)}</li>)}</ul>
          if (b.t === 'table') return (
            <table key={i} style={{ borderCollapse: 'collapse', margin: '6px 0', fontSize: TYPO.sizes.xs }}>
              <tbody>{b.rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={{ border: `1px solid ${COLORS.line}`, padding: '3px 7px', fontWeight: ri === 0 ? 700 : 400, background: ri === 0 ? COLORS.bgSoft : 'transparent' }}>{fmt(c, i + '-' + ri + '-' + ci)}</td>)}</tr>)}</tbody>
            </table>
          )
          return <p key={i} style={{ margin: '4px 0' }}>{fmt(b.x, i)}</p>
        })}
      </div>
    </Modal>
  )
}

export default function HelpHint({ text, section, label }) {
  const [hover, setHover] = useState(false)
  const [modal, setModal] = useState(false)
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
      <span
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        onClick={(e) => { e.stopPropagation(); setModal(true) }}
        title={section ? 'Klik = otvoriť manuál' : ''}
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: '50%', background: COLORS.nav, color: '#fff', fontSize: 11, fontWeight: 700, marginLeft: 5, flexShrink: 0, lineHeight: 1, userSelect: 'none' }}
      >?</span>
      {hover && text && (
        <span onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{ position: 'absolute', top: '120%', left: 0, zIndex: 5000, width: 360, maxHeight: 440, overflowY: 'auto', background: '#1f2937', color: '#fff', borderRadius: RADIUS.md, padding: '10px 12px', fontSize: TYPO.sizes.xs, fontWeight: 400, lineHeight: 1.55, textTransform: 'none', letterSpacing: 0, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
          {riadkyHintu(text).map((riadok, i) => (
            riadok.trim()
              ? <div key={'hr' + i} style={{ marginTop: i ? 5 : 0 }}>{fmt(riadok, 'hh' + i)}</div>
              : <div key={'hr' + i} style={{ height: 5 }} />
          ))}
          {section && <span onClick={(e) => { e.stopPropagation(); setModal(true) }} style={{ display: 'inline-block', marginTop: 6, color: '#7dd3fc', cursor: 'pointer', fontWeight: 600 }}>Otvoriť manuál (sek. {section}) →</span>}
        </span>
      )}
      <ManualModal open={modal} section={section} onClose={() => setModal(false)} />
    </span>
  )
}
